import React, { useEffect, useRef } from 'react';
import TrackerIcon from './TrackerIcon';

interface Props {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Sticky footer rendered inside the dialog (e.g. Cancel/Submit) — matches tracker.css's `.dlg-foot`. */
  footer?: React.ReactNode;
  /** Passed straight to the native <dialog>'s style, e.g. to widen a specific dialog like the original page does. */
  style?: React.CSSProperties;
}

/** A native <dialog>, styled entirely by tracker.css's `.rct:not(#_) .dlg-head/.dlg-body/.dlg-foot` and
 * `body.rct-body:not(#_) dialog` rules (border, radius, ::backdrop, sticky head/foot) — matches the old
 * page's `<dialog>` elements in catalyst_tracker/templates/catalyst_tracker/index.html exactly. */
const TrackerModal: React.FC<Props> = ({ title, onClose, children, footer, style }) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!el.open) el.showModal();
    const onCancel = (e: Event) => { e.preventDefault(); onClose(); };
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // "rct" here, not just on the page's outer wrapper: CatalystTrackerPage renders this dialog as a
    // sibling *after* its `.rct` div closes (native <dialog> escapes normal layout via showModal() anyway),
    // so every tracker.css rule — all scoped `.rct:not(#_) ...`, including the `all: revert` Tailwind
    // reset at the top of the file — needs `.rct` on an actual ancestor of this dialog's own content to
    // apply at all. Without it the dialog renders with zero tracker.css styling (and Tailwind's base
    // reset still stripping default input/button chrome): plain unstyled text, no borders, no spacing.
    <dialog ref={ref} className="rct" style={style} onClose={onClose}>
      <div className="dlg-head">
        <h2>{title}</h2>
        <button type="button" className="ib sm tip-r" data-close title="Close" aria-label="Close" onClick={onClose}>
          <TrackerIcon name="x" />
        </button>
      </div>
      <div className="dlg-body">{children}</div>
      {footer && <div className="dlg-foot">{footer}</div>}
    </dialog>
  );
};

export default TrackerModal;
